import { useEffect, useRef, useState } from 'react'
import { cachedAccess, type StopAccess } from '../sync/access'
import { FOUND_LOCATIONS, pendingFoundPhotos, saveFoundNote, saveFoundPhoto } from '../sync/findings'
import { compressPhoto } from '../sync/photo'
import type { DriverStop } from '../sync/route'
import type { FindingRecord } from '../sync/types'
import { TAP, button, card, color, container, h1, muted, page, textButton } from '../ui'

/**
 * ADD WHAT YOU FOUND (Brad, 2026-09-14). A driver who finds the gate is really the
 * second one, or the grit has moved, writes it here. It sits BESIDE what the customer
 * said and never replaces it; the stop screen shows it first next time.
 *
 * Every field is optional and saves on its own the moment the driver leaves it - to
 * the phone first, uploaded when there is signal. A photo here is a reference photo
 * for the next visit, not proof of this delivery.
 */
export function FoundScreen({ stop, onBack }: { stop: DriverStop; onBack: () => void }) {
  const [access, setAccess] = useState<StopAccess | undefined>(undefined)
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Record<string, boolean>>({})
  const [pending, setPending] = useState<FindingRecord[]>([])
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})

  const refresh = async () => {
    setAccess(await cachedAccess(stop.uuid))
    setPending(await pendingFoundPhotos(stop.uuid))
  }

  useEffect(() => {
    void refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stop.uuid])

  const current = (key: string) => access?.found?.[key]?.text ?? ''
  const value = (key: string) => (drafts[key] !== undefined ? drafts[key] : current(key))

  const saveNote = async (key: string) => {
    const text = drafts[key]
    if (text === undefined || text.trim() === current(key).trim()) return
    await saveFoundNote(stop.uuid, key, text)
    setSaved((prev) => ({ ...prev, [key]: true }))
    await refresh()
  }

  const onPhoto = async (key: string, photoKind: string, file: File | undefined) => {
    if (!file) return
    setBusy(key)
    setErr('')
    try {
      const c = await compressPhoto(file)
      await saveFoundPhoto(stop.uuid, photoKind, c.blob)
      await refresh()
    } catch (e) {
      setErr(`Photo failed (${e instanceof Error ? e.message : String(e)}). Try again.`)
    } finally {
      setBusy('')
      const input = fileRefs.current[key]
      if (input) input.value = ''
    }
  }

  return (
    <div style={page}>
      <div style={container}>
        <button style={{ ...textButton, paddingLeft: 0 }} onClick={onBack}>‹ Stop</button>
        <div style={muted}>{stop.name}</div>
        <div style={h1}>Add what you found</div>
        <div style={{ ...muted, marginBottom: 12 }}>
          Anything that will help next time. It goes beside what the customer told us, never over it. Leave anything blank.
        </div>
        {err && <div style={{ ...card, background: '#fef2f2', borderColor: color.red }}>{err}</div>}

        {FOUND_LOCATIONS.map((loc) => {
          const photos = pending.filter((p) => p.photoKind === loc.photoKind)
          return (
            <div key={loc.key} style={card}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ fontSize: 20, fontWeight: 800 }}>{loc.label}</div>
                {saved[loc.key] && <span style={{ color: color.greenDark, fontWeight: 700 }}>Saved ✓</span>}
              </div>
              <textarea
                aria-label={`What you found: ${loc.label}`}
                value={value(loc.key)}
                onChange={(e) => setDrafts({ ...drafts, [loc.key]: e.target.value })}
                onBlur={() => saveNote(loc.key)}
                rows={2}
                style={{ width: '100%', boxSizing: 'border-box', minHeight: TAP * 1.5, marginTop: 8, padding: 12, fontSize: 18, border: `1px solid ${color.greyLight}`, borderRadius: 10, fontFamily: 'inherit' }}
              />
              <input
                ref={(el) => {
                  fileRefs.current[loc.key] = el
                }}
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                onChange={(e) => onPhoto(loc.key, loc.photoKind, e.target.files?.[0])}
              />
              <button
                style={{ ...button(color.blue, color.white, busy === loc.key), marginTop: 8, width: '100%' }}
                disabled={busy === loc.key}
                onClick={() => fileRefs.current[loc.key]?.click()}
              >
                {busy === loc.key ? 'Saving photo…' : `Photo of the ${loc.label.toLowerCase()}`}
              </button>
              {photos.length > 0 && (
                <div style={{ ...muted, fontSize: 15, marginTop: 6 }}>
                  {photos.length} photo{photos.length === 1 ? '' : 's'} saved on this phone, uploading when there is signal
                </div>
              )}
            </div>
          )
        })}

        <button style={{ ...button(color.green), width: '100%', minHeight: 72, fontSize: 22, marginBottom: 90 }} onClick={onBack}>
          Done ▸
        </button>
      </div>
    </div>
  )
}
